import React, { lazy, Suspense } from "react";
import { Await } from "react-router-dom";
import { ArticleContext } from "@app/types/Page";
import CustomError from "@components/Error/CustomError";
import Spinner from "@components/Spinner/Spinner";
import { TRouteWithRedirect, useRouteContext } from "@sphtech/web2-core/ssr";
import { HttpStatusCode } from "axios";

import { BuilderContext } from "./Builder/utils/types";
import { StaticPageContext } from "./Static/utils/types";
import { StoryThreadContext } from "./StoryThread/utilities/types";

const ArticleWrapper = lazy(() => import("@pages/Article/ArticleWrapper"));
const StoryThreadPage = lazy(
  () => import("@pages/StoryThread/StoryThreadPage")
);
const StaticPage = lazy(() => import("@pages/Static/StaticPage"));
const CatchAllBuilderRoutes = lazy(
  () => import("@pages/Builder/CatchAllBuilderRoutes")
);

export default function DefaultPageHandler(): React.ReactElement {
  const { context } =
    useRouteContext<
      TRouteWithRedirect<
        ArticleContext | StoryThreadContext | StaticPageContext,
        string
      >
    >();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  return (
    <Await resolve={context.payload} errorElement={<CustomError />}>
      {(
        context:
          | ArticleContext
          | StoryThreadContext
          | StaticPageContext
          | BuilderContext
      ) => {
        const { kind, data } = context;

        // Load article page content.
        if (kind === "art") {
          return (
            <Suspense fallback={<Spinner />}>
              <ArticleWrapper data={data} />
            </Suspense>
          );
        }

        // Load storythread page content.
        if (kind === "storythread") {
          const { entity } = context;

          return (
            <Suspense fallback={<Spinner />}>
              <StoryThreadPage {...{ data, entity }} />
            </Suspense>
          );
        }

        // Load static page content.
        if (kind === "static") {
          return (
            <Suspense fallback={<Spinner />}>
              <StaticPage {...{ data }} />
            </Suspense>
          );
        }

        // Load builder page content.
        if (kind === "builder") {
          return (
            <Suspense fallback={<Spinner />}>
              <CatchAllBuilderRoutes seoFooterData={data.seoFooterData} />
            </Suspense>
          );
        }

        // Default error page.
        return <CustomError statusCode={HttpStatusCode.NotFound} />;
      }}
    </Await>
  );
}
